import { ApiService } from '../ApiService';
import { Photo, PhotosView } from '../../models/Photo';
import { CommentsView } from '../../models/Comment';
import { MembersView } from '../../models/Member';
import { DiscoverPhotos } from '../../models/Discover';
import { plainToClass } from '~/models/transform';
import { BaseListRequestParams } from '~/models/BaseRequestParams';

export interface IUploadPhotoOptions {
  eventId?: number;
  isPrivate?: boolean;
  isPrimary?: boolean;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  caption?: string;
}

export interface IUploadPhotoToMessageOptions {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

export interface IPhotosListParams extends BaseListRequestParams {
  withDetails?: boolean;
}

export class PhotosService {
  // LISTS
  static async getPhotosLatest(params?: IPhotosListParams): Promise<PhotosView> {
    const response = await ApiService.get<PhotosView>('photos/latest', params);
    return plainToClass(PhotosView, response);
  }

  static async getPhotosLatestFollowings(params?: IPhotosListParams): Promise<PhotosView> {
    const response = await ApiService.get<PhotosView>('photos/latest/followings', params);
    return plainToClass(PhotosView, response);
  }

  static async getPhotosLatestPrivate(params?: IPhotosListParams): Promise<PhotosView> {
    const response = await ApiService.get<PhotosView>('photos/latest/private', params);
    return plainToClass(PhotosView, response);
  }

  static async getPhotosLatestLiked(params?: IPhotosListParams): Promise<PhotosView> {
    const response = await ApiService.get<PhotosView>('photos/latest/liked', params);
    return plainToClass(PhotosView, response);
  }

  /**
   * Fetches the discovery overview with consolidated photo lists.
   * Returns up to 6 photos per list: latest, following, private, and liked.
   */
  static async GetDiscoverLists(): Promise<DiscoverPhotos> {
    const response = await ApiService.get<DiscoverPhotos>('photos/discover');
    return plainToClass(DiscoverPhotos, response);
  }

  // SINGLE PHOTO OPERATIONS
  static async getPhotoDetails(id: number | string): Promise<Photo> {
    const response = await ApiService.get<Photo>(`photos/${id}`);
    return plainToClass(Photo, response);
  }

  static async likePhoto(id: number): Promise<any> {
    return ApiService.post(`photos/${id}/like`, {});
  }

  static async unlikePhoto(id: number): Promise<any> {
    return ApiService.post(`photos/${id}/unlike`, {});
  }

  static async setPhotoAsProfile(id: number): Promise<void> {
    return ApiService.patch(`photos/${id}/profile`, {});
  }

  static async setPhotoAsPrivate(id: number): Promise<void> {
    return ApiService.patch(`photos/${id}/private`, {});
  }

  static async requestPhotoReview(id: number): Promise<void> {
    return ApiService.post(`photos/${id}/review`, {});
  }

  static async editPhotoCaption(id: number, caption: string): Promise<void> {
    return ApiService.patch(`photos/${id}/caption`, { Caption: caption });
  }

  static async deletePhoto(id: number): Promise<void> {
    return ApiService.delete(`photos/${id}`);
  }

  // PHOTO UPLOAD
  private static createPhotoFormData(image: string | File): FormData {
    const formData = new FormData();

    // Handle File or URI string
    if (typeof image === 'string') {
      // Native platform with URI
      formData.append('Image', {
        uri: image,
        type: 'image/jpeg',
        name: 'photo.jpg',
      } as any);
    } else {
      // Web platform with File
      // Ensure we're sending the actual File object
      formData.append('Image', image);
    }

    return formData;
  }

  static async uploadPhoto(
    image: string | File,
    options: IUploadPhotoOptions = {}
  ): Promise<void> {
    const formData = this.createPhotoFormData(image);
    
    // Build query parameters
    const params: Record<string, string> = {};
    
    if (options.eventId) {
      params.eventId = options.eventId.toString();
    }
    
    if (options.isPrivate) {
      params.isPrivate = 'true';
    }
    
    if (options.isPrimary) {
      params.isPrimary = 'true';
    }
    
    if (options.x || options.y || options.width || options.height) {
      params.x = (options.x ?? 0).toString();
      params.y = (options.y ?? 0).toString();
      params.width = (options.width ?? 0).toString();
      params.height = (options.height ?? 0).toString();
    }
    
    if (options.caption) {
      params.caption = options.caption;
    }

    return ApiService.request('POST', 'photos/upload', formData, params);
  }

  static async uploadPhotoToMessage(
    image: string | File,
    messageId: number,
    options: IUploadPhotoToMessageOptions = {}
  ): Promise<void> {
    const formData = this.createPhotoFormData(image);
    
    // Build query parameters
    const params: Record<string, string> = {};
    
    if (options.x || options.y || options.width || options.height) {
      params.x = (options.x ?? 0).toString();
      params.y = (options.y ?? 0).toString();
      params.width = (options.width ?? 0).toString();
      params.height = (options.height ?? 0).toString();
    }

    return await ApiService.request('POST', `photos/upload/message/${messageId}`, formData, params);
  }

  // COMMENTS AND LIKES
  static async getPhotoComments(id: number, params?: BaseListRequestParams): Promise<CommentsView> {
    const response = await ApiService.get<CommentsView>(`photos/${id}/comments`, params);
    return plainToClass(CommentsView, response);
  }

  static async getPhotoLikes(id: number, params?: BaseListRequestParams): Promise<MembersView> {
    const response = await ApiService.get<MembersView>(`photos/${id}/likes`, params);
    return plainToClass(MembersView, response);
  }

  static async addPhotoComment(id: number, text: string): Promise<void> {
    return ApiService.post(`photos/${id}/comment`, { Text: text });
  }
} 